import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import AppFooter from '../components/AppFooter';

export default function CookiePolicyPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="cookie-policy-page">
      {/* Header */}
      <div className="bg-white border-b border-beige">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all"
            data-testid="cookie-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Cookie size={20} className="text-sage" />
            <span className="text-lg font-medium text-charcoal">Cookie Policy</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-xl border border-beige p-6 sm:p-10 shadow-sm">
            <h1 className="text-2xl sm:text-3xl font-light text-charcoal mb-1">
              Kura Stash Cookie Policy
            </h1>
            <p className="text-sm text-gray-soft mb-8">Last Updated: March 6, 2026</p>

            <div className="space-y-8 text-charcoal leading-relaxed">
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">1. What Are Cookies?</h2>
                <p className="text-sm text-charcoal/80">
                  Cookies are small text files stored on your device when you visit a website. They help the site remember your preferences and provide a better user experience. This policy explains which cookies Kura Stash (<a href="https://kurastash.com" className="text-sage hover:text-sage-dark underline">kurastash.com</a>) uses and why.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">2. Cookies We Use</h2>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Session Cookie</p>
                    <p className="text-sm text-charcoal/80">
                      <strong>Name:</strong> <code className="bg-cream px-1 rounded text-xs">session_token</code><br />
                      <strong>Purpose:</strong> Keeps you signed in to your account securely.<br />
                      <strong>Type:</strong> Strictly necessary (httpOnly, secure).<br />
                      <strong>Duration:</strong> 7 days from sign-in. Cleared when you log out.
                    </p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Cloudflare Cookies</p>
                    <p className="text-sm text-charcoal/80">
                      <strong>Name:</strong> <code className="bg-cream px-1 rounded text-xs">__cf_bm</code>, <code className="bg-cream px-1 rounded text-xs">__cfruid</code><br />
                      <strong>Purpose:</strong> Set by Cloudflare for bot detection, security, and performance optimization.<br />
                      <strong>Type:</strong> Strictly necessary.<br />
                      <strong>Duration:</strong> Session or up to 30 minutes.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">3. Cookies We Do Not Use</h2>
                <ul className="space-y-2 text-sm text-charcoal/80">
                  <li className="flex gap-2">
                    <span className="text-sage mt-0.5">&#8226;</span>
                    <span><strong>No advertising or tracking cookies.</strong> We do not use any cookies for advertising, remarketing, or cross-site tracking.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-sage mt-0.5">&#8226;</span>
                    <span><strong>No analytics cookies.</strong> We do not use Google Analytics, Facebook Pixel, or any similar third-party analytics services.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-sage mt-0.5">&#8226;</span>
                    <span><strong>No third-party cookies</strong> beyond those set by Cloudflare for security purposes.</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">4. Local Storage</h2>
                <p className="text-sm text-charcoal/80 mb-3">
                  In addition to cookies, we use your browser's local storage for the following purposes:
                </p>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Session Token Backup</p>
                    <p className="text-sm text-charcoal/80">A copy of your session token is stored in local storage as a fallback for browsers that restrict third-party cookies (e.g., Safari). Cleared on logout.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Cached User Data</p>
                    <p className="text-sm text-charcoal/80">Basic user profile data (name, email) is cached locally for faster page loads. Cleared on logout.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">5. Managing Cookies</h2>
                <p className="text-sm text-charcoal/80">
                  Because we only use strictly necessary cookies, disabling them may prevent the app from functioning correctly (e.g., you won't be able to stay signed in). You can clear cookies at any time through your browser settings. Logging out of Kura Stash will also clear your session cookie.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">6. Changes to This Policy</h2>
                <p className="text-sm text-charcoal/80">
                  If we introduce new types of cookies in the future, we will update this policy and notify you. We are committed to only using cookies that are necessary for the service.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">7. Contact Us</h2>
                <p className="text-sm text-charcoal/80">
                  For questions about our cookie practices, please contact us at{' '}
                  <a href="mailto:support@kurastash.com" className="text-sage hover:text-sage-dark underline">
                    support@kurastash.com
                  </a>.
                </p>
              </section>
            </div>
          </div>
        </article>
      </div>

      <AppFooter variant="minimal" />
    </div>
  );
}
